import { Injectable } from '@angular/core';
import { allLanguageCodes, defaultLanguage, Language } from '@interfaces/translation-interface';
import { firstValueFrom, of } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';
import { CommonVariable } from 'src/app/app.constants';
import { UserManagementService } from 'src/app/services/user-management.service';

export type ContextProvenance =
  | 'explicit'
  | 'saved'
  | 'profile'
  | 'edge'
  | 'locale'
  | 'timezone'
  | 'fallback';

export interface ResolvedField<T> {
  value: T;
  provenance: ContextProvenance;
  dirty: boolean;
}

export interface InitialUserContext {
  countryCode: ResolvedField<string>;
  dialCode: ResolvedField<string>;
  currency: ResolvedField<string>;
  language: ResolvedField<string>;
}

const ALLOWED_EDGE_COUNTRIES = new Set([
  'GB', 'IE', 'US', 'CA', 'AU', 'NZ', 'FR', 'DE', 'IT', 'ES', 'NL', 'BE', 'CH', 'AT', 'PT', 'SE', 'NO', 'DK', 'FI',
]);

const TIMEZONE_COUNTRY_MAP: Record<string, string> = {
  'Europe/London': 'GB',
  'Europe/Dublin': 'IE',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Zurich': 'CH',
  'Europe/Vienna': 'AT',
  'Europe/Lisbon': 'PT',
  'Europe/Stockholm': 'SE',
  'Europe/Oslo': 'NO',
  'Europe/Copenhagen': 'DK',
  'Europe/Helsinki': 'FI',
  'America/New_York': 'US',
  'America/Toronto': 'CA',
  'Australia/Sydney': 'AU',
  'Pacific/Auckland': 'NZ',
};

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  GB: 'GBP',
  IE: 'EUR',
  US: 'USD',
  CA: 'CAD',
  AU: 'AUD',
  NZ: 'NZD',
  FR: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  CH: 'CHF',
  AT: 'EUR',
  PT: 'EUR',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  FI: 'EUR',
};

const COUNTRY_DIAL_CODE_MAP: Record<string, string> = {
  GB: '44',
  IE: '353',
  US: '1',
  CA: '1',
  AU: '61',
  NZ: '64',
  FR: '33',
  DE: '49',
  IT: '39',
  ES: '34',
  NL: '31',
  BE: '32',
  CH: '41',
  AT: '43',
  PT: '351',
  SE: '46',
  NO: '47',
  DK: '45',
  FI: '358',
};

const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
  GB: 'en',
  IE: 'en',
  US: 'en',
  CA: 'en',
  AU: 'en',
  NZ: 'en',
  FR: 'fr',
  DE: 'de',
  IT: 'it',
  ES: 'es',
};

const WEAK_PROVENANCE = new Set<ContextProvenance>(['fallback', 'timezone', 'locale']);

@Injectable({
  providedIn: 'root',
})
export class InitialUserContextService {
  private context: InitialUserContext | null = null;

  constructor(
    private readonly userManagementService: UserManagementService
  ) {}

  resolveInitialContext(options?: {
    explicitCountry?: string;
    explicitCurrency?: string;
    explicitLanguage?: string;
    profileCountry?: string;
    profileCurrency?: string;
    profileLanguage?: string;
  }): InitialUserContext {
    const countryCode = this.resolveCountry(options);
    const currency = this.resolveCurrency(countryCode.value, options);
    const language = this.resolveLanguage(countryCode.value, options);
    const dialCode = this.resolveDialCode(countryCode);

    this.context = {
      countryCode,
      dialCode,
      currency,
      language,
    };

    this.applyToCommonVariable(this.context);
    return this.context;
  }

  getContext(): InitialUserContext | null {
    return this.context;
  }

  markDirty(field: keyof InitialUserContext): void {
    if (!this.context) {
      return;
    }
    this.context[field].dirty = true;
    this.context[field].provenance = 'explicit';
  }

  async confirmFromIpInBackground(): Promise<void> {
    if (!this.context) {
      this.resolveInitialContext();
    }

    try {
      const ipData = await firstValueFrom(
        this.userManagementService.getUserCountryBaseOnIP().pipe(
          timeout(5000),
          take(1),
          catchError(() => of(null))
        )
      );

      if (!ipData || !this.context) {
        return;
      }

      const ipCountry = this.normalizeCountryCode(ipData.country_code);
      const ipCurrency = typeof ipData.currency?.code === 'string' ? ipData.currency.code : null;
      const ipLanguage = ipData.location?.languages?.[0]?.code;
      const ipDialCode = ipData.location?.calling_code
        ? `+${String(ipData.location.calling_code).replace(/^\+/, '')}`
        : null;

      this.maybeUpdateField(this.context.countryCode, ipCountry, 'edge');
      this.maybeUpdateField(this.context.currency, ipCurrency, 'edge');
      this.maybeUpdateField(
        this.context.language,
        ipLanguage && allLanguageCodes.includes(ipLanguage as Language) ? ipLanguage : null,
        'edge'
      );

      if (ipDialCode && !this.context.dialCode.dirty && WEAK_PROVENANCE.has(this.context.dialCode.provenance)) {
        this.context.dialCode.value = ipDialCode;
        this.context.dialCode.provenance = 'edge';
      }

      this.applyToCommonVariable(this.context);
    } catch {
      // Background confirmation must never block public flows.
    }
  }

  private resolveCountry(options?: {
    explicitCountry?: string;
    profileCountry?: string;
  }): ResolvedField<string> {
    const explicit = this.normalizeCountryCode(options?.explicitCountry);
    if (explicit) {
      return { value: explicit, provenance: 'explicit', dirty: false };
    }

    const savedCountry = this.normalizeCountryCode(localStorage.getItem('country_code') || '');
    if (savedCountry) {
      return { value: savedCountry, provenance: 'saved', dirty: false };
    }

    const profileCountry = this.normalizeCountryCode(options?.profileCountry);
    if (profileCountry) {
      return { value: profileCountry, provenance: 'profile', dirty: false };
    }

    const edgeCountry = this.getEdgeCountryHint();
    if (edgeCountry) {
      return { value: edgeCountry, provenance: 'edge', dirty: false };
    }

    const localeCountry = this.getLocaleRegion();
    if (localeCountry) {
      return { value: localeCountry, provenance: 'locale', dirty: false };
    }

    const timezoneCountry = this.getTimezoneCountry();
    if (timezoneCountry) {
      return { value: timezoneCountry, provenance: 'timezone', dirty: false };
    }

    return { value: 'GB', provenance: 'fallback', dirty: false };
  }

  private resolveCurrency(
    countryCode: string,
    options?: { explicitCurrency?: string; profileCurrency?: string }
  ): ResolvedField<string> {
    const explicit = options?.explicitCurrency?.toUpperCase();
    if (explicit) {
      return { value: explicit, provenance: 'explicit', dirty: false };
    }

    const savedCurrency = localStorage.getItem('preferred_currency_code') || localStorage.getItem('user_currency');
    if (savedCurrency && savedCurrency !== 'undefined') {
      return { value: savedCurrency.toUpperCase(), provenance: 'saved', dirty: false };
    }

    const profileCurrency = options?.profileCurrency?.toUpperCase();
    if (profileCurrency) {
      return { value: profileCurrency, provenance: 'profile', dirty: false };
    }

    const mappedCurrency = COUNTRY_CURRENCY_MAP[countryCode];
    if (mappedCurrency) {
      return { value: mappedCurrency, provenance: 'locale', dirty: false };
    }

    return { value: 'GBP', provenance: 'fallback', dirty: false };
  }

  private resolveLanguage(
    countryCode: string,
    options?: { explicitLanguage?: string; profileLanguage?: string }
  ): ResolvedField<string> {
    const explicit = options?.explicitLanguage?.toLowerCase();
    if (explicit && allLanguageCodes.includes(explicit as Language)) {
      return { value: explicit, provenance: 'explicit', dirty: false };
    }

    const profileLanguage = options?.profileLanguage?.toLowerCase();
    if (profileLanguage && allLanguageCodes.includes(profileLanguage as Language)) {
      return { value: profileLanguage, provenance: 'profile', dirty: false };
    }

    const savedLanguage = localStorage.getItem('language_code');
    if (savedLanguage && allLanguageCodes.includes(savedLanguage as Language)) {
      return { value: savedLanguage, provenance: 'saved', dirty: false };
    }

    const browserLanguage = this.getBrowserBaseLanguage();
    if (browserLanguage) {
      return { value: browserLanguage, provenance: 'locale', dirty: false };
    }

    const countryLanguage = COUNTRY_LANGUAGE_MAP[countryCode];
    if (countryLanguage && allLanguageCodes.includes(countryLanguage as Language)) {
      return { value: countryLanguage, provenance: 'locale', dirty: false };
    }

    return { value: defaultLanguage, provenance: 'fallback', dirty: false };
  }

  private resolveDialCode(country: ResolvedField<string>): ResolvedField<string> {
    const dialDigits = COUNTRY_DIAL_CODE_MAP[country.value] || COUNTRY_DIAL_CODE_MAP.GB;
    return {
      value: `+${dialDigits}`,
      provenance: country.provenance,
      dirty: false,
    };
  }

  private maybeUpdateField(
    field: ResolvedField<string>,
    nextValue: string | null,
    provenance: ContextProvenance
  ): void {
    if (!nextValue || field.dirty || !WEAK_PROVENANCE.has(field.provenance)) {
      return;
    }
    field.value = nextValue;
    field.provenance = provenance;
  }

  private applyToCommonVariable(context: InitialUserContext): void {
    // In-memory defaults are always refreshed so the current render is correct.
    CommonVariable.countryCode = context.countryCode.value;
    CommonVariable.preferredCurrency = context.currency.value;
    CommonVariable.preferredLanguage = context.language.value;

    // Only persist strong provenance. Persisting weak values (fallback/locale/
    // timezone) would resurface them as 'saved' on the next visit and permanently
    // block later IP/edge correction.
    if (!WEAK_PROVENANCE.has(context.language.provenance)) {
      localStorage.setItem('language_code', context.language.value);
    }
    if (!WEAK_PROVENANCE.has(context.currency.provenance)) {
      localStorage.setItem('user_currency', context.currency.value);
    }
  }

  private getEdgeCountryHint(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)ls_geo=([A-Za-z]{2})(?:;|$)/);
    const country = match?.[1]?.toUpperCase() ?? '';
    return ALLOWED_EDGE_COUNTRIES.has(country) ? country : null;
  }

  private getLocaleRegion(): string | null {
    try {
      const locale = navigator.languages?.[0] || navigator.language || 'en-GB';
      const parsed = new Intl.Locale(locale.replace('_', '-'));
      const region = parsed.region?.toUpperCase();
      return region && ALLOWED_EDGE_COUNTRIES.has(region) ? region : null;
    } catch {
      return null;
    }
  }

  private getBrowserBaseLanguage(): string | null {
    const locale = navigator.languages?.[0] || navigator.language || '';
    const baseLanguage = locale.split('-')[0]?.toLowerCase();
    return baseLanguage && allLanguageCodes.includes(baseLanguage as Language) ? baseLanguage : null;
  }

  private getTimezoneCountry(): string | null {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return TIMEZONE_COUNTRY_MAP[timezone] ?? null;
    } catch {
      return null;
    }
  }

  private normalizeCountryCode(value?: string | null): string | null {
    const country = value?.trim().toUpperCase() ?? '';
    return country.length === 2 && ALLOWED_EDGE_COUNTRIES.has(country) ? country : null;
  }
}
